import { useState, useEffect } from "react";
import { GetLocalAuthToken, HandleExpiredToken } from "../services/MoblyzeAuth";

// Error codes the transcript view maps to its own copy. Anything else carries
// the worker's message (or a generic one) in `error.message`.
export const MYA_TRANSCRIPT_ERRORS = {
  FORBIDDEN: "forbidden",
  NOT_FOUND: "not_found",
  FAILED: "failed",
};

// Moblyze staff only: reads a Mya thread (blocks, raw transcript, proposals,
// feedback) from the AI worker's admin route. Fetches only once `shouldFetch`
// is true and there is a thread id, so the transcript is never requested
// until an admin opens it.
//
// The response is conversation content (PII): it is kept in memory only —
// never logged, never written to storage.
const useMyaThreadTranscript = (threadId, shouldFetch = false) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!threadId || !shouldFetch) return undefined;

    let cancelled = false;

    const fetchTranscript = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await GetLocalAuthToken();

        const response = await fetch(
          `${process.env.REACT_APP_AI_WORKER_DOMAIN}/admin/mya/threads/${encodeURIComponent(threadId)}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          // 401: the session expired — sign out as everywhere else.
          if (response.status === 401) {
            HandleExpiredToken();
            return;
          }
          // 403 means "not Moblyze staff", not an expired token: don't sign
          // the user out, tell them they can't see it.
          if (response.status === 403) {
            if (!cancelled) setError({ code: MYA_TRANSCRIPT_ERRORS.FORBIDDEN });
            return;
          }
          if (response.status === 404) {
            if (!cancelled) setError({ code: MYA_TRANSCRIPT_ERRORS.NOT_FOUND });
            return;
          }
          let message = "Failed to load the Client Mya transcript";
          try {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              const result = await response.json();
              message = result?.error || message;
            }
          } catch {
            // Keep the default message.
          }
          if (!cancelled) setError({ code: MYA_TRANSCRIPT_ERRORS.FAILED, message });
          return;
        }

        const result = await response.json();
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) {
          setError({
            code: MYA_TRANSCRIPT_ERRORS.FAILED,
            message: "Failed to load the Client Mya transcript",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTranscript();

    return () => {
      cancelled = true;
    };
  }, [threadId, shouldFetch]);

  return { data, loading, error };
};

export default useMyaThreadTranscript;
